import { useAppTranslation } from "@/utils";
import { Button, Flex, LegacyCard, Radio, Typography } from "@payping/web-components";
import { FC, useState } from "react";
import { GuaranteeType } from "@payping/api/app/api/bnplMerchantTypes";
import NoWarrantyIcon from "./Icons/NoWarranty";
import SalaryDeductionIcon from "./Icons/SalaryDeduction";
import PromissoryIcon from "./Icons/PromissoryIcon";
import { PlanData } from ".";

enum ContractType {
  withContract = "1",
  withoutContract = "2",
}
interface GuaranteeTypes {
  title: string;
  description: string;
  icon: any;
  value: number;
  active?: boolean;
}
interface ContractResponse {
  contractIsMandatory: boolean;
  guaranteeType: GuaranteeType | undefined;
}

type Props = {
  onNext: (value: ContractResponse) => void;
  onPrev: () => void;
  loading: boolean;
  planData: PlanData;
  selectedGuarantyItem: GuaranteeType | undefined;
  setSelectedGuarantyItem: (value: GuaranteeType) => void;
};

const PlanContract: FC<Props> = ({
  onNext,
  onPrev,
  loading,
  planData,
  setSelectedGuarantyItem,
  selectedGuarantyItem,
}) => {
  const { t } = useAppTranslation();
  const [contractType, setContractType] = useState<string>(
    planData.contractIsMandatory === true ||
      planData.contractIsMandatory === null ||
      planData.contractIsMandatory === undefined
      ? ContractType.withContract
      : ContractType.withoutContract,
  );
  const [guaranteeItems, setGuaranteeItems] = useState<Array<GuaranteeTypes>>([
    {
      icon: NoWarrantyIcon,
      value: GuaranteeType.None,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeItemTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeItemDescription"),
      active: planData.guaranteeType === GuaranteeType.None,
    },
    // {
    //   icon: ChequeIcon,
    //   value: GuaranteeType.Cheque,
    //   title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.chequeTitle"),
    //   description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.chequeDescription"),
    //   active: false,
    // },
    {
      icon: PromissoryIcon,
      value: GuaranteeType.Promissory,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.promissoryTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.promissoryDescription"),
      active: planData.guaranteeType === GuaranteeType.Promissory,
    },
    {
      icon: SalaryDeductionIcon,
      value: GuaranteeType.Salary,
      title: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.salaryTitle"),
      description: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.salaryDescription"),
      active: planData.guaranteeType === GuaranteeType.Salary,
    },
  ]);

  const options = [
    { label: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.withContract"), value: ContractType.withContract },
    {
      label: t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.withoutContract"),
      value: ContractType.withoutContract,
    },
  ];

  const onChangeGuaranty = (item: GuaranteeTypes) => {
    setGuaranteeItems((prev) =>
      prev.map((g) => ({
        ...g,
        active: item.value === g.value,
      })),
    );
    setSelectedGuarantyItem(item.value);
  };

  const allowSubmit = () => {
    const guaranteeActiveItems = guaranteeItems.filter((a) => a.active).length;
    if (guaranteeActiveItems > 0) {
      return true;
    }
    return false;
  };

  const next = () => {
    const data: ContractResponse = {
      contractIsMandatory: contractType === ContractType.withContract,
      guaranteeType: selectedGuarantyItem,
    };
    console.log("src/pages/Shop/Bnpl/Plans/PlanContract.tsx -> data:", data);
    onNext(data);
  };

  return (
    <Flex vertical gap={"large"} className="tw:w-full tw:max-w-160 tw:max-lg:max-w-full">
      <Flex vertical>
        <Typography.Title level={5} className="tw:mt-1!">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.title")}
        </Typography.Title>
        <Typography.Text className="tw:mt-0!">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.description")}
        </Typography.Text>

        <Flex className="tw:mt-4!">
          <Radio.Group
            onChange={(e) => setContractType(e.target.value as ContractType)}
            className="tw:w-full"
            block
            options={options}
            value={contractType}
            defaultValue={ContractType.withContract}
            optionType="button"
          />
        </Flex>
        <Typography.Title level={5} className="tw:mt-5!">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.setGuarantee")}
        </Typography.Title>
        <Typography.Text className="tw:m-0!">
          {t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.guaranteeDescription")}
        </Typography.Text>

        <Flex vertical gap={"small"} className="tw:mt-2!">
          {guaranteeItems.map((item, index) => {
            const IconComponent = item.icon;

            return (
              <LegacyCard.Ant
                key={index}
                onClick={() => onChangeGuaranty(item)}
                className={`tw:mt-2! tw:w-full tw:cursor-pointer ${
                  item.active ? "tw:border-solid! tw:border-color-primary!" : ""
                }`}
              >
                <Flex gap="middle">
                  <IconComponent width={40} height={40} />
                  <Flex vertical justify="center">
                    <Typography.Title level={5} className="tw:m-0!">
                      {item.title}
                    </Typography.Title>
                    <Typography.Text className="tw:mt-2!">{item.description}</Typography.Text>
                  </Flex>
                </Flex>
              </LegacyCard.Ant>
            );
          })}
        </Flex>
      </Flex>
      <Flex gap={"small"}>
        <Button block={false} onClick={onPrev} className="tw:px-8!" loading={false} type="default">
          {t("common.previousStep")}
        </Button>
        <Button
          block={false}
          disabled={!allowSubmit()}
          onClick={next}
          className="tw:px-8!"
          loading={loading}
          type="primary"
        >
          {selectedGuarantyItem === GuaranteeType.Salary
            ? t("common.saveAndContinue")
            : t("shop.bnpl.addInstallmentPlan.steps.PlanContract.form.saveAndCreate")}
        </Button>
      </Flex>
    </Flex>
  );
};

export default PlanContract;
